import FormDataServer from 'form-data';
import { COLORS } from '@ertelecom/ui-react/assets/js/constants';
import { isEmpty } from '~/src/helpers/parse';
import { declOfNum, integerDivision } from '~/src/helpers/numbers';
import { isServerSide } from '~/src/helpers/ssr';
import { getRequiredEquipmentAndVasTotalPrice } from '~/src/components/TariffDetail/data';

const MONO_VILLAGE_URL_PART = '/mono-village';
const BUNDLES_VILLAGE_URL_PART = '/village';

export const num2str = (n, texts) => {
    if (!texts) {
        return '';
    }
    n = Math.abs(n) % 100;
    const n1 = n % 10;
    if (n > 10 && n < 20) {
        return texts[2];
    }
    if (n1 > 1 && n1 < 5) {
        return texts[1];
    }
    if (n1 === 1) {
        return texts[0];
    }
    return texts[2];
};

export const replaceCityVars = (data, cityObject) => {
    if (!data) {
        return data;
    }

    if ('string' === typeof data) {
        return data.replace('%cityIn%', cityObject.cityIn);
    }

    let result = data;
    if (Array.isArray(data)) {
        result = [];
        data.forEach(prop => {
            result.push(replaceCityVars(prop, cityObject));
        });
        return result;
    } else if ('object' === typeof data) {
        result = { ...data };
        Object.keys(result).forEach(key => {
            const prop = result[key];
            result[key] = replaceCityVars(prop, cityObject);
        });
    }

    return result;
};

export const getTariffPrice = (tariff, type, isAuth = false) => {
    switch (type) {
        case 'internet':
            return getMonoTariffPrice(tariff, isAuth);
        case 'bundles':
            return getBundlePrice(tariff, isAuth);
    }
    return 0;
};

export const getTariffSpeed = (tariff, type) => {
    switch (type) {
        case 'internet':
            return tariff.speed;
        case 'bundles':
            return tariff.speed_int;
    }
    return 0;
};

export const getTariffId = (tariff, type) => {
    switch (type) {
        case 'internet':
            return tariff.tariff_id;
        case 'bundles':
            return tariff.sale_package_id;
    }
    return 0;
};

const promoPriceAvailable = (tariff, type, isAuth) => {
    switch (type) {
        case 'internet':
            if (tariff.price_promo && Number(tariff.price_promo) &&
                tariff.period_promo && Number(tariff.period_promo) &&
                (
                    (tariff.promo_for_guest && tariff.promo_for_client) ||
                    (tariff.promo_for_client && isAuth) ||
                    (tariff.promo_for_guest && !isAuth)
                )) {
                return true;
            }
            break;
        case 'bundles':
            if (tariff.promo_price2in1 && Number(tariff.promo_price2in1) &&
                tariff.promo_period && Number(tariff.promo_period) &&
                (
                    (tariff.promo_for_guest && tariff.promo_for_client) ||
                    (tariff.promo_for_client && isAuth) ||
                    (tariff.promo_for_guest && !isAuth)
                )) {
                return true;
            }
            break;
    }

    return false;
};

export const getMonoTariffPrice = (tariff, isAuth) => {
    const requiredEquipmentAndVasTotalPrice = getRequiredEquipmentAndVasTotalPrice(tariff);

    if (promoPriceAvailable(tariff, 'internet', isAuth)) {
        const formattedTariff = formattingTariffForCard(tariff, isAuth);
        return formattedTariff.priceAction + requiredEquipmentAndVasTotalPrice;
    }

    return tariff.price + requiredEquipmentAndVasTotalPrice;
};

export const getBundlePrice = (tariff, isAuth) => {
    const requiredEquipmentAndVasTotalPrice = getRequiredEquipmentAndVasTotalPrice(tariff);

    if (promoPriceAvailable(tariff, 'bundles', isAuth)) {
        return Number(tariff.promo_price2in1) + requiredEquipmentAndVasTotalPrice;
    }

    return Number(tariff.price2in1) + requiredEquipmentAndVasTotalPrice;
};

export const getBundleChannelsCount = (tariff) => {
    return tariff.salePackage2in1
        ? Number(tariff.salePackage2in1.sd_count) +
        Number(tariff.salePackage2in1.hd_count)
        : 0;
};

export const formattingTariffForCard = (tariff, isAuth) => {
    const defaultColor = '#000';
    const isCottage = tariff.is_cottage;
    const freePackets = tariff.salePackage2in1 ? tariff.salePackage2in1.free_packets : 0;
    // Доступность промо-цены: если не передан isAuth - всегда true
    const promoPriceAvail = 'boolean' !== typeof isAuth ||
        promoPriceAvailable(tariff, 'internet', isAuth);
    return ({
        speedAction: tariff.speed_bonus,
        speedActionPeriod: tariff.speed_bonus_duration,
        headingColor: tariff.description ? tariff.description.color ||
            defaultColor : defaultColor,
        description: tariff.description ? tariff.description.description : '',
        alias: tariff.description ? tariff.description.alias : '',
        speed: tariff.speed,
        price: Number(tariff.price),
        priceAction: promoPriceAvail ? tariff.price_promo : null,
        priceActionPeriod: promoPriceAvail ? tariff.period_promo : null,
        title: tariff.tariff_name_display ? tariff.tariff_name_display : '',
        productType: '40',
        salePackageId: tariff.tariff_id,
        productId: tariff.tariff_id,
        imageInHead: true,
        promoted: tariff.promote,
        location: '/internet' + (isCottage ? MONO_VILLAGE_URL_PART : ''),
        additionalProducts: tariff.additionalProducts || [],
        isCottage: isCottage,
        freePackets: freePackets,
        availableGuest: Boolean(tariff.available_guest ? tariff.available_guest : 0),
        availableClient: Boolean(tariff.available_client ? tariff.available_client : 0),
        advantages: tariff.advantages,
        includes: tariff.includes,
        priceIncludedVas: tariff.price_included_vas ? Number(tariff.price_included_vas) : null,
        type: 'internet',
        productIdForBilling: 5,
        priceConnect: tariff.connect_price,
        isSelfOrder: tariff.is_self_order ?? tariff.salePackage3in1?.is_self_order ??
            tariff.salePackage2in1?.is_self_order,
        fullSaleId: tariff.full_sale_id ?? tariff.salePackage3in1?.full_sale_id ??
            tariff.salePackage2in1?.full_sale_id ?? undefined,
        colorText: tariff.description?.color_text ? tariff.description.color_text : COLORS.black,
        imageBackground: tariff.description?.image_background ? `url(${tariff.description.image_background})` : COLORS.white,
    });
};

export const formattingBundleForCard = (tariff, isAuth) => {
    const defaultColor = '#34c270';
    const isCottage = tariff.salePackage2in1.is_cottage;
    // Доступность промо-цены: если не передан isAuth - всегда true
    const promoPriceAvail = 'boolean' !== typeof isAuth ||
        promoPriceAvailable(tariff, 'bundles', isAuth);
    return ({
        speedAction: tariff.speed_bonus,
        speedActionPeriod: tariff.speed_bonus_duration,
        headingColor: tariff.description ? tariff.description.color ||
            defaultColor : defaultColor,
        description: tariff.description ? tariff.description.description : '',
        alias: tariff.description ? tariff.description.alias : '',
        speed: tariff.speed_int,
        price: tariff.price2in1,
        priceAction: promoPriceAvail ? tariff.promo_price2in1 : null,
        priceActionPeriod: promoPriceAvail ? tariff.promo_period : null,
        title: tariff.description ? (tariff.description.title ||
            tariff.description.tariff_name) : '',
        productType: 100,
        descriptionFull: tariff.description_full ? tariff.description_full : '',
        productId: tariff.sale_package_id,
        imageInHead: false,
        promoted: tariff.description?.promoted,
        location: '/bundles' + (isCottage ? BUNDLES_VILLAGE_URL_PART : ''),
        hdCount: Number(tariff.salePackage2in1.hd_count),
        sdCount: Number(tariff.salePackage2in1.sd_count),
        analogCount: Number(tariff.salePackage2in1.analog_count),
        additionalProducts: tariff.additionalProducts || [],
        isCottage: isCottage,
        freePackets: tariff.salePackage2in1.free_packets,
        availableGuest: Boolean(tariff.description.available_guest),
        availableClient: Boolean(tariff.description.available_client),
        advantages: tariff.advantages,
        includes: tariff.includes,
        type: 'bundles',
        productIdForBilling: 0,
        priceConnect: Number(tariff.price_connect_internet) +
            Number(tariff.price_connect_tv),
        priceIncludedVas: Number(tariff.price_included_vas),
        isSelfOrder: tariff.is_self_order ?? tariff.salePackage3in1?.is_self_order ??
            tariff.salePackage2in1?.is_self_order,
        fullSaleId: tariff.full_sale_id ?? tariff.salePackage3in1?.full_sale_id ??
            tariff.salePackage2in1?.full_sale_id ?? undefined,
        colorText: tariff.description?.color_text ? tariff.description.color_text : COLORS.black,
        imageBackground: tariff.description?.image_background ? `url(${tariff.description.image_background})` : COLORS.white,
    });
};

export const formattingVideoForCard = (tariff) => {
    const defaultColor = '#34c270';
    return ({
        headingColor: tariff.description ? tariff.description.color ||
            defaultColor : defaultColor,
        description: tariff.description ? tariff.description.description : '',
        price: tariff.price2in1,
        title: tariff.description ? (tariff.description.title ||
            tariff.description.tariff_name) : '',
        productType: 100,
        promoted: tariff.description?.promoted,
        additionalProducts: tariff.additionalProducts || [],
        type: 'video',
        saleAction: tariff.saleAction,
        additionalProductsDesc: tariff.addDescription || '',
        archiveOption: tariff.archiveOption || '',
    });
};

export const formattingRoutersForCard = routers => {
    return (
        routers.map(router => {
            const leasingOptions = [];
            if (router.leasingDuration && router.leasingPrice) {
                leasingOptions.push({
                    duration: router.leasingDuration,
                    price: router.leasingPrice,
                });
            }
            if (router.leasingDurationSec && router.leasingPriceSec) {
                leasingOptions.push({
                    duration: router.leasingDurationSec,
                    price: router.leasingPriceSec,
                });
            }
            return ({
                ...router,
                typeEquipment: 'router',
                description: router.description,
                leasingOptions,
                image: router.mainImage,
                materialsEnsId: router.materialsEnsId,
                title: router.materialsEnsName,
                price: router.posessPrice,
                speed: router.speed,
                promo: router.promo,
            });
        })
    );

};

export const formattingTvBoxesForCard = (tvBoxes, isXpon) => {
    const movixProWifiMaterialEnsId = 96756;
    const movixProMaterialEnsId = 80491;
    const movixProVoiceMaterialEnsId = 101183;

    return (
        tvBoxes.filter(function(tvBox) {
                if (
                    tvBox.materials_ens_id === movixProWifiMaterialEnsId ||
                    (
                       isXpon &&
                       (
                           tvBox.materials_ens_id === movixProMaterialEnsId ||
                           tvBox.materials_ens_id === movixProVoiceMaterialEnsId
                       )
                    )
                ) {
                   return false;
                }
                return true;
            }).map(tvBox => {
                const leasingOptions = [];
                if (tvBox.leasing_dur && tvBox.leasing_mon_price) {
                    leasingOptions.push({
                        duration: tvBox.leasing_dur,
                        price: tvBox.leasing_mon_price,
                    });
                }
                if (tvBox.leasing_dur_two && tvBox.leasing_mon_price_two) {
                    leasingOptions.push({
                        duration: tvBox.leasing_dur_two,
                        price: tvBox.leasing_mon_price_two,
                    });
                }
                return ({
                    ...tvBox,
                    typeEquipment: 'tvBox',
                    description: tvBox.description_main,
                    leasingOptions,
                    image: tvBox.image,
                    materialsEnsId: tvBox.materials_ens_id,
                    title: tvBox.mat_name,
                    price: tvBox.posess_price,
                    promo: tvBox.promo,
                });
            })
    );
};

export function toBasicFormData(params) {
    let form;
    if (isServerSide()) {
        form = new FormDataServer();
    } else {
        form = new FormData();
    }

    for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
            if (!isEmpty(params[key])) {
                form.append(key, params[key]);
            }
        }
    }
    return form;
}

/**
 * @param {Number} milliseconds
 * @return {string}
 */
export function intervalToHumanReadable(milliseconds) {
    const SECONDS_IN_YEAR = 31536000;
    const SECONDS_IN_DAY = 86400;
    const SECONDS_IN_HOUR = 3600;
    const seconds = integerDivision(milliseconds, 1000);
    const numHours = Math.floor(((seconds % SECONDS_IN_YEAR) % SECONDS_IN_DAY) / SECONDS_IN_HOUR);
    const numMinutes = Math.floor((((seconds % SECONDS_IN_YEAR) % SECONDS_IN_DAY) % SECONDS_IN_HOUR) / 60);
    const out = [];
    if (numHours > 0) {
        out.push(numHours + ' ' + declOfNum(numHours, ['час', 'часа', 'часов']));
    }
    if (numMinutes > 0) {
        out.push(numMinutes + ' ' + declOfNum(numMinutes, ['минуту', 'минуты', 'минут']));
    }

    if (numMinutes === 0 && numHours === 0) {
        const numSeconds = (((seconds % SECONDS_IN_YEAR) % SECONDS_IN_DAY) % SECONDS_IN_HOUR) % 60;
        return numSeconds + ' ' + declOfNum(numSeconds, ['секунду', 'секунды', 'секунд']);
    }

    return out.join(' ');
}
